import { useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { MailOutlined, PauseCircleOutlined, UserOutlined } from '@ant-design/icons';
import BasicContainer from '@copilot/common/components/containers/basic';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import UserSettings from './user/userSettings';
import EmailSettings from './email';
import PauseSettings from './pause';
import isNil from 'lodash/isNil';
import { Tabs } from 'antd';
import { useMemo } from 'react';
import styles from './settingsPage.module.less';
import { HostedAuthCompletionModal } from '@copilot/common/pages/settings/linkedInAuthResults';

type SettingsPageProps = {
    hideEmails?: boolean;
    hideLinkedIn?: boolean;
    hidePause?: boolean;
    hideUser?: boolean;
};

const containerPadding = '20px';

function SettingsPage(props: SettingsPageProps) {
    const { tab = 'user' } = useParams<{ tab?: string }>();
    const { hideEmails, hideLinkedIn, hidePause, hideUser } = props;
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const navigate = useNavigate();

    function onTabChange(key: string) {
        const path = key === 'user' ? '/settings' : `/settings/${key}`;
        navigate(path);
    }

    const availableTabs = useMemo(() => {
        const hasLoadedOrgMember = !isNil(activeMember);
        if (!hasLoadedOrgMember) return [];

        const tabs = [];
        if (!hideUser) {
            tabs.push({
                key: 'user',
                label: (
                    <>
                        <UserOutlined /> User Settings
                    </>
                ),
                children: (
                    <BasicContainer style={{ padding: containerPadding }}>
                        <UserSettings orgMember={activeMember} hideLinkedIn={hideLinkedIn} />
                    </BasicContainer>
                ),
            });
        }

        if (!hideEmails) {
            tabs.push({
                key: 'email',
                label: (
                    <>
                        <MailOutlined /> Email Notification
                    </>
                ),
                children: (
                    <BasicContainer
                        className={styles.emailTabContent}
                        style={{
                            padding: containerPadding,
                        }}
                    >
                        <EmailSettings user={activeMember} />
                    </BasicContainer>
                ),
            });
        }

        if (!hidePause) {
            tabs.push({
                key: 'pause',
                label: (
                    <>
                        <PauseCircleOutlined /> Pause All Campaigns
                    </>
                ),
                children: (
                    <BasicContainer style={{ padding: containerPadding }}>
                        <PauseSettings orgMemberId={activeMember.id} />
                    </BasicContainer>
                ),
            });
        }

        return tabs;
    }, [hideEmails, hideLinkedIn, hidePause, hideUser, activeMember]);

    if (isNil(activeMember)) return null;

    return (
        <div className={styles.settingsPageWrapper}>
            <div className={styles.pageHeader}>
                <h2>Settings</h2>
            </div>

            <Tabs
                className={styles.settingsTabs}
                defaultActiveKey={tab}
                onChange={onTabChange}
                //prevents us from rendering non-visible tabs
                destroyInactiveTabPane
                items={availableTabs}
            />
            <HostedAuthCompletionModal />
        </div>
    );
}

export default SettingsPage;
